import { Copy, Crown, Users, UserCheck, HandCoins, Clock, TrendingUp, Gift } from "lucide-react";
import { toast } from "sonner";
import { usd, fmtDate } from "@/lib/api";
import { useFetch, PageHeader, Panel, StatusBadge, Table, Empty, Loading, StatCard } from "@/components/common";
import { EarnChart } from "@/pages/Dashboard";

const SRC = { deposit: "Deposit", trading: "Trading", first_deposit_bonus: "First deposit bonus" };
const rule = (t, v) => (t === "flat" ? `${usd(v)} flat` : `${v}%`);

function ShareBox({ code, premium }) {
  const link = `${window.location.origin}/register?ref=${code}`;
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  return (
    <div className={`nx-card p-6 ${premium ? "border-[#10B981]/40 bg-gradient-to-br from-[#121722] to-[#0F1B1A]" : ""}`} data-testid="referral-share-box">
      <p className="text-[11px] uppercase tracking-wider text-slate-400">Your referral code</p>
      <div className="mt-2 flex items-center gap-3">
        <p className="num text-3xl font-semibold text-[#10B981]" data-testid="referral-code">{code}</p>
        <button onClick={() => copy(code)} className="nx-btn-ghost px-2 py-1.5" data-testid="copy-referral-code"><Copy size={14} /></button>
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#1E2738] bg-[#0B0E14] px-3 py-2">
        <p className="num flex-1 truncate text-xs text-slate-300" data-testid="referral-link">{link}</p>
        <button onClick={() => copy(link)} className="text-slate-400 hover:text-[#10B981]" data-testid="copy-referral-link"><Copy size={14} /></button>
      </div>
    </div>
  );
}

const CommTable = ({ rows }) => (
  <Table head={["Date", "From", "Source", "Ref / Txn ID", "Rule", "Amount", "Status"]} testid="commissions-table">
    {!rows.length && <Empty cols={7} text="No commissions yet" />}
    {rows.map((c) => (
      <tr key={c.id}>
        <td className="nx-td">{fmtDate(c.created_at)}</td>
        <td className="nx-td num">{c.source_user_code}</td>
        <td className="nx-td">{SRC[c.source_type]}</td>
        <td className="nx-td num text-xs">{c.source_ref?.slice(0, 28)}</td>
        <td className="nx-td">{rule(c.rule_type, c.rule_value)}</td>
        <td className="nx-td num text-[#10B981]">{usd(c.amount)}</td>
        <td className="nx-td"><StatusBadge status={c.status} /></td>
      </tr>
    ))}
  </Table>
);

const ReferredTable = ({ rows }) => (
  <Table head={["User ID", "Name", "Joined", "Deposited"]} testid="referred-users-table">
    {!rows.length && <Empty cols={4} text="No referrals yet — share your code to get started" />}
    {rows.map((r) => (
      <tr key={r.user_code}>
        <td className="nx-td num">{r.user_code}</td>
        <td className="nx-td">{r.name}</td>
        <td className="nx-td">{fmtDate(r.joined_at)}</td>
        <td className="nx-td"><StatusBadge status={r.deposited ? "active" : "pending"} /></td>
      </tr>
    ))}
  </Table>
);

function PartnerView({ d }) {
  const p = d.partner || {};
  return (
    <div className="space-y-6" data-testid="youtuber-dashboard">
      <PageHeader eyebrow="YouTuber Partner Program" title={<span className="flex items-center gap-3"><Crown className="text-[#10B981]" />Partner Hub</span>}
        subtitle={p.channel_name ? `Channel: ${p.channel_name}` : "Your premium partner dashboard"} />
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-5"><ShareBox code={d.referral_code} premium /></div>
        <div className="nx-card p-6 lg:col-span-7" data-testid="partner-terms">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Your commission terms</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[[Gift, "First deposit bonus", usd(p.first_deposit_bonus), "partner-first-deposit-bonus"],
              [HandCoins, "Later deposits", rule(p.deposit_type, p.deposit_value), "partner-deposit-rule"],
              [TrendingUp, "Trading", `${rule(p.trading_type, p.trading_value)}${p.trading_type === "flat" ? " / event" : " of returns"}`, "partner-trading-rule"]].map(([I, l, v, t]) => (
              <div key={l} className="rounded-lg border border-[#1E2738] bg-[#0B0E14] p-4">
                <I size={16} className="text-[#10B981]" />
                <p className="mt-3 text-xs text-slate-500">{l}</p>
                <p className="num mt-1 text-lg text-white" data-testid={t}>{v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Stats s={d.stats} />
      <Panel title="Approved earnings (last 30 days)"><EarnChart data={d.earnings_chart} testid="partner-earnings-chart" /></Panel>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Referred users"><ReferredTable rows={d.referred} /></Panel>
        <Panel title="Commission ledger"><CommTable rows={d.commissions} /></Panel>
      </div>
    </div>
  );
}

const Stats = ({ s }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
    <StatCard label="Referred users" value={s.referred_count} icon={Users} testid="ref-stat-count" />
    <StatCard label="Depositing users" value={s.active_referrals} icon={UserCheck} testid="ref-stat-active" />
    <StatCard label="Approved earnings" value={usd(s.approved_total)} icon={HandCoins} accent testid="ref-stat-approved" />
    <StatCard label="Pending approval" value={usd(s.pending_total)} icon={Clock} testid="ref-stat-pending" />
  </div>
);

export default function Referrals() {
  const { data: d, loading } = useFetch("/referrals");
  if (loading || !d) return <Loading />;
  if (d.is_youtuber) return <PartnerView d={d} />;
  const p = d.program;
  return (
    <div className="space-y-6" data-testid="referral-dashboard">
      <PageHeader eyebrow="Referral Program" title="Invite & earn" subtitle="Earn commissions when people you invite deposit and trade." />
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-7"><ShareBox code={d.referral_code} /></div>
        <div className="nx-card p-6 lg:col-span-5" data-testid="referral-program-rules">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Program rules</p>
          {p.enabled ? (
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex justify-between"><span className="text-slate-400">Per deposit</span><span className="num text-white">{rule(p.deposit_type, p.deposit_value)}</span></li>
              <li className="flex justify-between"><span className="text-slate-400">Per trading return</span><span className="num text-white">{rule(p.trading_type, p.trading_value)}</span></li>
              <li className="text-xs text-slate-500">Commissions are credited after admin approval.</li>
            </ul>
          ) : <p className="mt-4 text-sm text-slate-500">The referral program is currently paused.</p>}
        </div>
      </div>
      <Stats s={d.stats} />
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Referred users"><ReferredTable rows={d.referred} /></Panel>
        <Panel title="Commissions"><CommTable rows={d.commissions} /></Panel>
      </div>
    </div>
  );
}
